import { asArray } from "@/lib/asArray";
import SignoffRollup from "@/components/SignoffRollup";   // Batch 11 · A8
import { wsOpenParam, WS_OPEN_PARAM, openSlotOf } from "@/lib/worksheetLinks";   // openSlotOf: Step 11d-3 · find 4
import React, { useEffect, useState, useMemo } from "react";
import LoadingNote from "@/components/LoadingNote";   // Batch 9 · A2 / A16
import { Link, useSearchParams } from "react-router-dom";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { FolderTree, FileText, ExternalLink, RefreshCw, Library, Plus, EyeOff, Undo2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import AddFromLibraryDialog from "@/components/AddFromLibraryDialog";
import AddHeadFromLibraryDialog from "@/components/AddHeadFromLibraryDialog";
import Ref from "@/components/Ref";
import HeadFolderWorksheetDialog from "@/components/HeadFolderWorksheetDialog";
import SurlWorksheetDialog, { TXT as SURL_TXT } from "@/components/SurlWorksheetDialog";   // Batch 26 · Add-on 1
import HeadFoldersSignoffTotal from "@/components/HeadFoldersSignoffTotal";   // Step 11d-3 · find 7
import HeadFolderVouchingDialog from "@/components/HeadFolderVouchingDialog";
import CompletenessWorksheetDialog from "@/components/CompletenessWorksheetDialog";
import InventoryCountWorksheetDialog from "@/components/InventoryCountWorksheetDialog";
import EstimatesWorksheetDialog from "@/components/EstimatesWorksheetDialog";
import RelatedPartyWorksheetDialog from "@/components/RelatedPartyWorksheetDialog";
import WorksheetErrorBoundary from "@/components/WorksheetErrorBoundary";
import LeadSheetDialog from "@/components/LeadSheetDialog";
import ProceduresPerformedDialog from "@/components/ProceduresPerformedDialog";
import ObservationsDialog from "@/components/ObservationsDialog";
import api2 from "@/lib/api";
import { fmtDate } from "@/lib/dt";
import { toast } from "sonner";
import HeadFolderFormsListDialog from "@/components/HeadFolderFormsListDialog";
import { HfDeleteButton, HfDeleteDialog } from "@/components/HfDeleteDialog";
import { askConfirm, askPrompt } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)

// Round 3 final — Arabic plural rules for the "N files" count label.
// Uses Intl.PluralRules('ar') to classify: 0 → "0 ملفات",
// 1 → "ملف واحد", 2 → "ملفان", 3–10 → "N ملفات", 11+ → "N ملفًا".
const AR_PLURAL = new Intl.PluralRules("ar");
function filesCountLabel(n, lang, t) {
  if (lang !== "ar") {
    return n === 1
      ? `1 ${t("headFolders.filesSuffixOne")}`
      : `${n} ${t("headFolders.filesSuffix")}`;
  }
  if (n === 0) return "0 ملفات";
  const cat = AR_PLURAL.select(n);
  if (cat === "one") return "ملف واحد";
  if (cat === "two") return "ملفان";
  if (cat === "few") return `${n} ملفات`;
  if (cat === "many") return `${n} ملفًا`;
  return `${n} ملفات`;
}
/**
 * Batch 0 Phase 3 — Head Folders page.
 *
 * Auto-generated tree derived server-side from `coa_mappings.fs_mapping`
 * distinct values, one head folder per used FSLI code. Each head folder
 * carries the standard 01–06+ working-paper structure. Assignments +
 * move operations are logged in the audit trail (server-side).
 *
 * Batch 0 note: this page is the structural home. Phase 3 delivers the
 * auto-generated folder tree and per-file counts; a richer file-listing
 * UI + drag-to-move affordance ships in a later batch. Forms placed
 * under "head_folders" by the engagement profile (spec sheet 3 rows
 * 314(a) / 315(a)) are surfaced here as quick-open shortcuts.
 */
export default function HeadFolders() {
  const { currentEngId } = useEngagement();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  // C3a · item 10 — Add head from library.
  const [headLibOpen, setHeadLibOpen] = useState(false);
  // C3a · item 11 — inline per-head balance probe result cache.
  const [naProbe, setNaProbe] = useState({}); // fs_code -> {has_activity, ...}
  // C4 · AA2 — "Show voided" toggle for the head-folder tile view.
  const [showVoided, setShowVoided] = useState(false);
  // Round 8 · U4 — forms-list dialog (opened by 06/07 row click).
  const [formsListFor, setFormsListFor] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const [so, setSo] = useState(null);     // Batch 11 · A8 — sign-off roll-up
  const [urlParams, setUrlParams] = useSearchParams();   // Batch 27 · Add-on 3 · A: ?head=<code> (a search result) opens that head
  const headParam = urlParams.get("head") || "";
  // Step 11d-3 · Mars find 4: ?open=1201-06 names the folder slot "06 Forms / Worksheets" (e.g. the 314(a) link of the inventory
  // count worksheet): open that slot's list, as a click on the row does — it used to open a cut-off worksheet dialog with no body
  const openSlot = openSlotOf(urlParams.get("open") || "");
  const openSlotKey = openSlot ? `${openSlot.fs_code}-${openSlot.file_id}` : "";
  const load = async () => {
    if (!currentEngId) return;
    setLoading(true);
    try {
      const url = `/engagements/${currentEngId}/head-folders`
                    + (showVoided ? "?show_voided=true" : "");
      const [hf, prof] = await Promise.all([
        api.get(url, { _silent: true }),
        api.get(`/engagements/${currentEngId}/profile`, { _silent: true }),
      ]);
      setData(hf.data);
      setProfile(prof.data);
      api.get(`/engagements/${currentEngId}/signoffs`, { _silent: true }).then((r) => setSo(r.data)).catch(() => setSo(null));   // Batch 11 · A8
    } catch (e) {
      setData(null); setProfile(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [currentEngId, showVoided]);
  useEffect(() => {      // Batch 27 · Add-on 3 · A: bring the head a search result named into view
    if (!headParam || !data) return;
    const el = document.querySelector(`[data-testid="head-folder-${headParam}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "start" });
  }, [headParam, data]);

  useEffect(() => {
    if (!openSlotKey || !data) return;
    const [fs_code, file_id] = openSlotKey.split("-");
    setFormsListFor({ fs_code, file_id });
    const next = new URLSearchParams(urlParams); next.delete("open");
    setUrlParams(next, { replace: true });
  }, [openSlotKey, data]);   // eslint-disable-line react-hooks/exhaustive-deps -- once per link

  const placementForms = useMemo(
    () => profile?.form_placements?.head_folders || [], [profile]);

  if (!currentEngId) {
    return (
      <div className="space-y-4" data-testid="page-head-folders">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("pages.head_folders.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("pages.head_folders.purpose")}</p>
        </div>
        <Card><CardContent className="py-6 text-sm text-muted-foreground">{t("headFolders.selectEng")}</CardContent></Card>
      </div>
    );
  }

  const heads = data?.head_folders || [];
  const naHeads = data?.na_head_folders || [];
  const unassigned = data?.unassigned_head;

  // C3a · item 11 — lazily fetch balance probe when the auditor
  // hovers/focuses the disabled tooltip target. Result cached per head.
  const probeBalance = async (fs_code) => {
    if (naProbe[fs_code]) return;
    try {
      const r = await api.get(`/engagements/${currentEngId}/head-folders/${fs_code}/balance-check`, { _silent: true });
      setNaProbe((p) => ({ ...p, [fs_code]: r.data }));
    } catch { /* ignore */ }
  };

  // C3a · item 11 — Mark N/A action (reason required).
  const markNa = async (fs_code) => {
    const reason = await askPrompt(t("headFolders.markNaReasonPrompt"));
    if (!reason) return;
    try {
      await api.post(`/engagements/${currentEngId}/head-folders/${fs_code}/mark-na`,
                     { reason }, { _silent: true });
      toast.success(t("headFolders.markedNaOk"));
      await load();
    } catch (e) {
      const code = e?.response?.data?.detail?.code || e?.response?.data?.code;
      if (code === "HEAD_HAS_BALANCE") {
        toast.error(t("headFolders.hasBalanceTooltip"));
      } else {
        toast.error(String(e?.response?.data?.detail?.detail || e?.response?.data?.detail || e));
      }
    }
  };

  const restoreNa = async (fs_code) => {
    try {
      await api.post(`/engagements/${currentEngId}/head-folders/${fs_code}/restore`,
                     null, { _silent: true });
      toast.success(t("headFolders.restoredOk"));
      await load();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || e));
    }
  };

  // C3a · item 15 — flatten a head folder into a single ref-ordered
  // list. Standard files (01..07) and pinned forms/worksheets both
  // become rows; sort by ref suffix number so 314(a) still appears
  // adjacent to 1201-06 / 1201-07.
  // M1 / M2 · titles read from hfWs.row.{kind} on AR, "Estimate — {nm}"
  //           dedupes when nm is empty / equals "Estimate"; every row
  //           carries an `href` so 03/04/06/07 open the relevant page,
  //           plus a status distinct from the count.
  const flattenRows = (hf) => {
    const rows = [];
    for (const f of (hf.files || [])) {
      const stdHref =
        f.id === "01" ? `?openProc=${encodeURIComponent(hf.fs_code)}` :
        f.id === "02" ? `?openLead=${encodeURIComponent(hf.fs_code)}` :
        f.id === "05" ? `?openObs=${encodeURIComponent(hf.fs_code)}` :
        f.id === "03" ? `/sampling?fs=${encodeURIComponent(hf.fs_code)}` :
        f.id === "04" ? `?openVouch=${encodeURIComponent(hf.fs_code)}` :
        // 06 / 07 — "Forms / Worksheets" and "Index" rows act as
        // section markers for the pinned worksheets listed below in
        // this same accordion body. N1 fix (round 2): the earlier
        // `#{fs_code}` anchor did nothing visible when the folder was
        // already open. Use a non-navigating href marker so the row's
        // onClick handler can scroll to the first pinned row + toast
        // "no forms yet" if the list is empty.
        f.id === "06" ? (f.has_pinned_worksheet ? `?open=${encodeURIComponent(`${hf.fs_code}-06`)}` : `#__forms_${encodeURIComponent(hf.fs_code)}`) :
        f.id === "07" ? (f.has_pinned_worksheet ? `?open=${encodeURIComponent(`${hf.fs_code}-07`)}` : `#__forms_${encodeURIComponent(hf.fs_code)}`) :
        `#${encodeURIComponent(hf.fs_code)}`;
      // Batch 3 · 3.4 (R43) — the lead sheet reads "Ready" when the head has figures
      // Batch 27 · B (M34): the server's status of the file (procedures answered, completed, observations open …)
      const st = f.status;
      const status = st && st.state !== "none" ? (lang === "ar" ? st.text_ar : st.text_en) :
        f.id === "02" && f.ready ? t("headFolders.leadReady") :
        f.count > 0 ? filesCountLabel(f.count, lang, t) : t("common.empty");
      rows.push({
        kind: "standard",
        ref: `${hf.fs_code}-${f.id}`,
        sortKey: `10-${f.id}`,
        file: f,
        title:
          f.id === "01" ? t("proc.title") :
          f.id === "02" ? t("lead.title") :
          f.id === "05" ? t("obs.title") :
          (f.id === "07" && f.pinned_worksheet_kind === "surl") ? SURL_TXT[lang === "ar" ? "ar" : "en"].title :   // Batch 26 · Add-on 1
          (t(`headFolders.standardFiles.${f.id}`) || f.label),
        status,
        statusState: st?.state || "",
        count: f.count,
        href: stdHref,
      });
    }
    // Pinned forms + worksheets.
    const dynKindTitle = (k) => {
      const key = `hfWs.row.${k}`;
      const v = t(key);
      return (v && v !== key) ? v : null;
    };
    for (const f of (hf.files || [])) {
      for (const p of (f.pinned_forms || [])) {
        const isWs = !!p.worksheet_code;
        const ref = p.ref_no || `${hf.fs_code}-${f.id}`;
        const numMatch = ref.match(/-(\d+)/);
        const sortKey = numMatch
          ? `10-${String(numMatch[1]).padStart(2,"0")}-p`
          : `10-${f.id}-p`;
        let title = p.title_ar && lang === "ar" ? p.title_ar : p.title;
        // M1 — for dynamic worksheets, use hfWs.row.{kind}; append the
        // real estimate name only when it's distinct (avoids
        // "Estimate — Estimate").
        if (p.worksheet_kind && ["completeness","inventory_count","related_party","estimate"].includes(p.worksheet_kind)) {
          const base = dynKindTitle(p.worksheet_kind) || p.title;
          if (p.worksheet_kind === "estimate") {
            const nm = (p.title || "").replace(/^Estimate\s*—\s*/, "").trim();
            title = (nm && nm !== "Estimate") ? `${base} — ${nm}` : base;
          } else {
            title = base;
          }
        }
        rows.push({
          kind: isWs ? "worksheet" : "form",
          ref,
          sortKey,
          pin: p,
          folderFile: f,
          title,
          worksheet_kind: p.worksheet_kind,
          status: p.completed ? t("common.done") : t("common.pending"),
          count: null,
          href: isWs
            ? `?${wsOpenParam(p.worksheet_kind)}=${encodeURIComponent(p.worksheet_code)}`
            : `/planning?openForm=${encodeURIComponent(p.form_id)}`,
        });
      }
    }
    rows.sort((a, b) => a.sortKey.localeCompare(b.sortKey) || a.ref.localeCompare(b.ref));
    return rows;
  };

  return (
    <div className="space-y-4" data-testid="page-head-folders">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="head-folders-title">
            {t("pages.head_folders.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("pages.head_folders.purpose")}</p>
          {/* Step 11d-3 · find 7: the head folders on this page only (was the whole engagement's roll-up under "Sign-offs") */}
          <HeadFoldersSignoffTotal so={so} heads={heads} t={t} />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
            data-testid="head-folders-refresh">
            <RefreshCw className={`h-3.5 w-3.5 me-1.5 ${loading ? "animate-spin" : ""}`} />
            {t("headFolders.refresh")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setLibOpen(true)}
            data-testid="head-folders-add-from-library">
            <Library className="h-3.5 w-3.5 me-1.5" />
            {t("headFolders.addFromLibrary")}
          </Button>
          {/* C3a · item 10 — Add head from library. */}
          <Button variant="outline" size="sm" onClick={() => setHeadLibOpen(true)}
            data-testid="head-folders-add-head-from-library">
            <Plus className="h-3.5 w-3.5 me-1.5" />
            {t("headFolders.addHeadFromLibrary")}
          </Button>
          {/* C4 · AA2 — Show voided toggle. Voided tiles render
              struck-through with the standard "ملغى — reason · date ·
              actor" badge (same shape as the W9 page). */}
          <label className="ms-2 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground"
                   data-testid="head-folders-show-voided">
            <input type="checkbox" checked={showVoided}
                    onChange={e => setShowVoided(e.target.checked)}
                    data-testid="head-folders-show-voided-input" />
            {t("headFolders.showVoided") || "Show voided"}
          </label>
        </div>
      </div>

      {placementForms.length > 0 && (
        <Card data-testid="head-folders-forms-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4 text-emerald-600" />
              {t("placeholder.formsHeader")}
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                {placementForms.length}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-muted-foreground">{t("placeholder.formsHint")}</p>
            <ul className="divide-y rounded-md border border-slate-200 dark:border-slate-800">
              {placementForms.map((f) => (
                <li key={f.form_id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="break-words font-medium">{f.title}</div>
                  </div>
                  <Link to={`/planning?openForm=${encodeURIComponent(f.form_id)}`}
                    className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50/40 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-300"
                    data-testid={`head-folders-open-form-${f.form_id}`}>
                    {t("placeholder.openForm")} <ExternalLink className="h-3 w-3" />
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FolderTree className="h-4 w-4 text-emerald-600" />
            {t("headFolders.autoGenTitle")}
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
              {heads.length}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Batch 9 · A2: a loading line (with the seconds) while the list loads — never the empty text */}
          {loading && heads.length === 0 && <LoadingNote active data-testid="head-folders-loading" />}
          {!loading && heads.length === 0 && !unassigned && (
            <p className="text-sm text-muted-foreground" data-testid="head-folders-empty">
              {t("headFolders.emptyMapping")}
            </p>
          )}
          {/* Batch 27 · F (M36): heads with no scoping decision, with the way to decide them */}
          {(() => {
            const n = heads.filter((h) => h.scoping_decision === "not_decided").length;
            return n > 0 ? (
              <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
                   data-testid="head-folders-scoping-banner">
                {n === 1 ? t("headFolders.scopingBannerOne") : t("headFolders.scopingBanner").replace("{n}", String(n))}{" "}
                <Link to="/scoping" className="font-medium underline" data-testid="head-folders-scoping-link">{t("headFolders.scopingLink")}</Link>
              </div>
            ) : null;
          })()}
          {(heads.length > 0 || unassigned) && (
            <Accordion type="multiple" className="w-full" data-testid="head-folders-accordion"
                       key={headParam || "all"} defaultValue={headParam ? [headParam] : undefined}>
              {heads.map((hf) => (
                <AccordionItem key={hf.fs_code} value={hf.fs_code}
                                 data-testid={`head-folder-${hf.fs_code}`}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex flex-1 items-center gap-3 pe-2">
                      <FolderTree className="h-4 w-4 text-emerald-600" />
                      <div className="min-w-0 flex-1 text-start">
                        <div className="break-words text-sm font-semibold">
                          <span dir="ltr" className="me-1 font-mono">{hf.fs_code}</span>
                          — {(lang === "ar" ? hf.label_ar : hf.label) || hf.label}
                          {hf.added_manually && (
                            <span className="ms-2 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-800 dark:bg-sky-950/40 dark:text-sky-300"
                                  data-testid={`head-folder-${hf.fs_code}-added-badge`}>
                              {t("headFolders.addedBadge")}
                            </span>
                          )}
                        </div>
                        {(hf.section || hf.section_ar) && (
                          <div className="text-[11px] text-muted-foreground">{(lang === "ar" ? hf.section_ar : hf.section) || hf.section}</div>
                        )}
                        {so?.rollup?.folders?.[hf.fs_code] && (
                          <SignoffRollup counts={so.rollup.folders[hf.fs_code]} levels={so.levels} t={t} testid={`hf-signoff-rollup-${hf.fs_code}`} />
                        )}
                      </div>
                      {hf.scoping_decision === "not_decided" && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                              data-testid={`head-folder-${hf.fs_code}-scoping-badge`}>
                          {t("headFolders.scopingNotDecided")}
                        </span>
                      )}
                      {hf.done_of > 0 && (
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${hf.done === hf.done_of ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}
                              data-testid={`head-folder-${hf.fs_code}-done`}>
                          {lang === "ar" ? hf.done_text_ar : hf.done_text_en}
                        </span>
                      )}
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        {filesCountLabel(hf.total_files, lang, t)}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {/* C3a · item 15 — every file, worksheet and pinned
                        form is one row in a single ref-ordered list.
                        No tile grid. Pinned rows keep their own ref. */}
                    <ul className="divide-y rounded border" data-testid={`head-folder-${hf.fs_code}-list`}>
                      {flattenRows(hf).map((row) => {
                        if (row.kind === "standard") {
                          const f = row.file;
                          const to = row.href;
                          const inner = (
                            <>
                              <span className="flex min-w-0 items-center gap-2">
                                <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-mono font-semibold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                                  <Ref>{row.ref}</Ref>
                                </span>
                                <span className="break-words">{row.title}</span>
                              </span>
                              <span className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span data-testid={`head-folder-${hf.fs_code}-status-${f.id}`} data-state={row.statusState}
                                      className={row.statusState === "completed" ? "text-emerald-700 dark:text-emerald-400" : ""}>{row.status}</span>
                                <ExternalLink className="h-3 w-3 text-emerald-600" />
                                <HfDeleteButton t={t} testid={`head-folder-${hf.fs_code}-del-${f.id}`}
                                  onClick={() => setDelTarget({ kind: "standard", fs_code: hf.fs_code, category: f.id, ref: row.ref, title: row.title })} />
                              </span>
                            </>
                          );
                          // Round 8 · U4 — 06/07 rows open a dialog
                          // listing every pinned form + dynamic
                          // worksheet in the clicked slot. The old
                          // scroll-to-first-pinned handler only
                          // highlighted a row and left the owner
                          // thinking "10 files" was wrong.
                          if (f.id === "06" || f.id === "07") {
                            return (
                              <button key={row.ref} type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setFormsListFor({ fs_code: hf.fs_code, file_id: f.id });
                                      }}
                                      className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-sm hover:bg-emerald-50 dark:hover:bg-emerald-950/20"
                                      data-testid={`head-folder-${hf.fs_code}-row-${f.id}`}>
                                {inner}
                              </button>
                            );
                          }
                          const isExternal = to.startsWith("/");
                          return isExternal ? (
                            <Link key={row.ref} to={to}
                                  className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm hover:bg-emerald-50 dark:hover:bg-emerald-950/20"
                                  data-testid={`head-folder-${hf.fs_code}-row-${f.id}`}>
                              {inner}
                            </Link>
                          ) : (
                            <a key={row.ref} href={to}
                               className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm hover:bg-emerald-50 dark:hover:bg-emerald-950/20"
                               data-testid={`head-folder-${hf.fs_code}-row-${f.id}`}>
                              {inner}
                            </a>
                          );
                        }
                        // Pinned form / worksheet row.
                        const p = row.pin;
                        const to = row.href;
                        const testid = row.kind === "worksheet"
                          ? `head-folder-${hf.fs_code}-row-ws-${p.worksheet_code}`
                          : `head-folder-${hf.fs_code}-row-pin-${p.form_id}`;
                        return (
                          <Link key={row.ref} to={to}
                                className={`flex items-center justify-between gap-3 px-3 py-1.5 text-sm ${row.kind === "worksheet" ? "hover:bg-emerald-50 dark:hover:bg-emerald-950/20" : "hover:bg-sky-50 dark:hover:bg-sky-950/20"}`}
                                data-testid={testid}>
                            <span className="flex min-w-0 items-center gap-2">
                              <span className={`rounded px-1.5 py-0.5 text-[10px] font-mono font-semibold ${row.kind === "worksheet" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-sky-100 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300"}`}>
                                <Ref>{row.ref}</Ref>
                              </span>
                              <span className="break-words">{row.title}</span>
                              {row.worksheet_kind && (
                                <span className="ms-1 rounded bg-slate-100 px-1 py-0.5 text-[9px] font-medium text-slate-800 dark:bg-slate-800 dark:text-slate-300">
                                  {t(`hfWs.badge.${row.worksheet_kind}`)}
                                </span>
                              )}
                            </span>
                            <span className="flex items-center gap-3 text-xs text-muted-foreground">
                              {/* Round 9 · D5 — created-by · date on
                                   every head-folder worksheet row.
                                   Round 11 · D5 fix — date shows
                                   DD-MMM-YYYY (LTR-isolated) via
                                   fmtDate so AR renders cleanly. */}
                              {(row.pin?.created_by_name || row.pin?.created_at) && (
                                <span className="text-[10px]"
                                      data-testid={`${testid}-created`}>
                                  · {row.pin?.created_by_name || "—"}
                                  {row.pin?.created_at
                                    ? ` · ${fmtDate(row.pin.created_at)}`
                                    : ""}
                                </span>
                              )}
                              {row.status && <span data-testid={`${testid}-status`}>{row.status}</span>}
                              <ExternalLink className={`h-3 w-3 ${row.kind === "worksheet" ? "text-emerald-600" : "text-sky-600"}`} />
                              <HfDeleteButton t={t} testid={`${testid}-del`}
                                onClick={() => setDelTarget({
                                  kind: row.kind === "worksheet" && ["completeness", "inventory_count", "estimate", "related_party"].includes(p.worksheet_kind) ? "worksheet" : "form",
                                  fs_code: hf.fs_code, category: row.folderFile?.id || p.category || "", ref: row.ref, title: row.title,
                                  worksheet_code: p.worksheet_code || "", worksheet_kind: p.worksheet_kind || "", form_id: p.form_id || "" })} />
                            </span>
                          </Link>
                        );
                      })}
                    </ul>
                    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`hf-${hf.fs_code}-actions`}>
                      <Button size="sm" variant="outline"
                        data-testid={`hf-${hf.fs_code}-add-completeness`}
                        onClick={async () => {
                          try {
                            // T4 round 4 — N7 open-empty-first.
                            // If this head already has an EMPTY /
                            // unused completeness worksheet (no
                            // listing uploaded + no rows), jump
                            // straight to it instead of asking.
                            // Otherwise, if there are any existing
                            // worksheets, confirm before creating a
                            // new one.
                            const existingPins = asArray(hf.files).flatMap((f) => (
                              asArray(f.pinned_forms).filter((p) => p.worksheet_kind === "completeness")
                            ));
                            const empties = existingPins.filter((p) => (
                              !p.completed && !p.has_listing && (p.row_count ?? 0) === 0
                            ));
                            if (empties.length > 0) {
                              const target = empties[0];
                              const next = new URLSearchParams(window.location.search);
                              next.set(WS_OPEN_PARAM.completeness, target.worksheet_code);
                              window.history.pushState({}, "", `${window.location.pathname}?${next.toString()}`);
                              window.dispatchEvent(new PopStateEvent("popstate"));
                              return;
                            }
                            if (existingPins.length > 0) {
                              const ok = await askConfirm(
                                t("headFolders.addWorksheetConfirm")
                                  .replace("{type}", t("comp.title"))
                                  .replace("{head}", hf.fs_code)
                                  .replace("{n}", String(existingPins.length)));
                              if (!ok) return;
                            }
                            const viaAdd = !["4101","5101","1201","3203"].includes(hf.fs_code);
                            const r = await api2.post(`/engagements/${currentEngId}/completeness/${hf.fs_code}`,
                              { via_add_worksheet: viaAdd });
                            toast.success(`${t("comp.title")} — ${r.data.ref}`);
                            const next = new URLSearchParams(window.location.search);
                            next.set(WS_OPEN_PARAM.completeness, r.data.ref);
                            window.history.pushState({}, "", `${window.location.pathname}?${next.toString()}`);
                            window.dispatchEvent(new PopStateEvent("popstate"));
                            await load();
                          } catch (e) { toast.error(String(e?.response?.data?.detail || e)); }
                        }}>
                        <Plus className="h-3 w-3 me-1" /> {t("common.addWorksheet")}: {t("comp.title")}
                      </Button>
                      {hf.fs_code === "1201" && !asArray(hf.files).some((f) => asArray(f.pinned_forms).some((p) => p.worksheet_kind === "inventory_count")) && (
                        /* Batch 3 · 3.4 (R8) — hidden once the head already has an inventory-count worksheet */
                        <Button size="sm" variant="outline"
                          data-testid={`hf-${hf.fs_code}-add-inv-count`}
                          onClick={async () => {
                            try {
                              const existingPins = asArray(hf.files).flatMap((f) => (
                                asArray(f.pinned_forms).filter((p) => p.worksheet_kind === "inventory_count")
                              ));
                              const empties = existingPins.filter((p) => (
                                !p.completed && !p.has_listing && (p.row_count ?? 0) === 0
                              ));
                              if (empties.length > 0) {
                                const target = empties[0];
                                const next = new URLSearchParams(window.location.search);
                                next.set(WS_OPEN_PARAM.inventory_count, target.worksheet_code);
                                window.history.pushState({}, "", `${window.location.pathname}?${next.toString()}`);
                                window.dispatchEvent(new PopStateEvent("popstate"));
                                return;
                              }
                              if (existingPins.length > 0) {
                                const ok = await askConfirm(
                                  t("headFolders.addWorksheetConfirm")
                                    .replace("{type}", t("inv6.title"))
                                    .replace("{head}", hf.fs_code)
                                    .replace("{n}", String(existingPins.length)));
                                if (!ok) return;
                              }
                              const r = await api2.post(`/engagements/${currentEngId}/inventory-count`);
                              toast.success(`${t("inv6.title")} — ${r.data.ref}`);
                              const next = new URLSearchParams(window.location.search);
                              next.set(WS_OPEN_PARAM.inventory_count, r.data.ref);
                              window.history.pushState({}, "", `${window.location.pathname}?${next.toString()}`);
                              window.dispatchEvent(new PopStateEvent("popstate"));
                              await load();
                            } catch (e) { toast.error(String(e?.response?.data?.detail || e)); }
                          }}>
                          <Plus className="h-3 w-3 me-1" /> {t("common.addWorksheet")}: {t("inv6.title")}
                        </Button>
                      )}
                      <Button size="sm" variant="outline"
                        data-testid={`hf-${hf.fs_code}-add-estimate`}
                        onClick={async () => {
                          try {
                            const existing = asArray(hf.files).reduce((acc, f) => (
                              acc + asArray(f.pinned_forms).filter((p) => p.worksheet_kind === "estimate").length
                            ), 0);
                            if (existing > 0) {
                              const ok = await askConfirm(
                                t("headFolders.addWorksheetConfirm")
                                  .replace("{type}", t("est.title"))
                                  .replace("{head}", hf.fs_code)
                                  .replace("{n}", String(existing)));
                              if (!ok) return;
                            }
                            const name = (await askPrompt(t("est.estimatePlaceholder"))) || "Estimate";
                            const r = await api2.post(`/engagements/${currentEngId}/estimates`,
                              { fs_code: hf.fs_code, estimate_name: name });
                            toast.success(`${t("est.title")} — ${r.data.ref}`);
                            const next = new URLSearchParams(window.location.search);
                            next.set(WS_OPEN_PARAM.estimate, r.data.ref);
                            window.history.pushState({}, "", `${window.location.pathname}?${next.toString()}`);
                            window.dispatchEvent(new PopStateEvent("popstate"));
                            await load();
                          } catch (e) { toast.error(String(e?.response?.data?.detail || e)); }
                        }}>
                        <Plus className="h-3 w-3 me-1" /> {t("common.addWorksheet")}: {t("est.title")}
                      </Button>
                      <Button size="sm" variant="outline"
                        data-testid={`hf-${hf.fs_code}-add-rp`}
                        onClick={async () => {
                          try {
                            const existing = asArray(hf.files).reduce((acc, f) => (
                              acc + asArray(f.pinned_forms).filter((p) => p.worksheet_kind === "related_party").length
                            ), 0);
                            if (existing > 0) {
                              const ok = await askConfirm(
                                t("headFolders.addWorksheetConfirm")
                                  .replace("{type}", t("rp.title"))
                                  .replace("{head}", hf.fs_code)
                                  .replace("{n}", String(existing)));
                              if (!ok) return;
                            }
                            const inDefault = ["1211","3210","3108"].includes(hf.fs_code);
                            const r = await api2.post(`/engagements/${currentEngId}/related-parties`,
                              { fs_code: hf.fs_code, via_add_worksheet: !inDefault });
                            toast.success(`${t("rp.title")} — ${r.data.ref}`);
                            const next = new URLSearchParams(window.location.search);
                            next.set(WS_OPEN_PARAM.related_party, r.data.ref);
                            window.history.pushState({}, "", `${window.location.pathname}?${next.toString()}`);
                            window.dispatchEvent(new PopStateEvent("popstate"));
                            await load();
                          } catch (e) { toast.error(String(e?.response?.data?.detail || e)); }
                        }}>
                        <Plus className="h-3 w-3 me-1" /> {t("common.addWorksheet")}: {t("rp.title")}
                      </Button>
                      {/* C3a · item 11 — Mark N/A. Enabled only when head
                          has no balance / GL movement. Button hovers
                          probe /balance-check so the tooltip is accurate. */}
                      <Button size="sm" variant="outline"
                              disabled={!!(naProbe[hf.fs_code]?.has_activity)}
                              title={naProbe[hf.fs_code]?.has_activity ? t("headFolders.hasBalanceTooltip") : undefined}
                              onMouseEnter={() => probeBalance(hf.fs_code)}
                              onFocus={() => probeBalance(hf.fs_code)}
                              onClick={() => markNa(hf.fs_code)}
                              data-testid={`hf-${hf.fs_code}-mark-na`}>
                        <EyeOff className="h-3 w-3 me-1" /> {t("headFolders.markNa")}
                      </Button>
                      {naProbe[hf.fs_code]?.has_activity && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-amber-800 dark:text-amber-300"
                              data-testid={`hf-${hf.fs_code}-balance-warn`}>
                          <AlertTriangle className="h-3 w-3" />
                          {t("headFolders.hasBalanceTooltip")}
                        </span>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
              {unassigned && (
                <AccordionItem value="__unassigned__" data-testid="head-folder-unassigned">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex flex-1 items-center gap-3 pe-2">
                      <FolderTree className="h-4 w-4 text-amber-600" />
                      <div className="min-w-0 flex-1 text-start">
                        <div className="break-words text-sm font-semibold text-amber-900 dark:text-amber-200">
                          {t("headFolders.unassigned")}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {t("headFolders.unassignedHint")}
                        </div>
                      </div>
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                        {unassigned.total_files}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2 lg:grid-cols-3">
                      {unassigned.files.map((f) => (
                        <div key={f.id}
                             className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50/40 px-3 py-2 text-sm dark:border-amber-900 dark:bg-amber-950/20"
                             data-testid={`head-folder-unassigned-file-${f.id}`}>
                          <div className="flex items-center gap-2">
                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-mono font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">{f.id}</span>
                            <span>{f.label}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">{f.count}</span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          )}
        </CardContent>
      </Card>
      <AddFromLibraryDialog open={libOpen} onOpenChange={setLibOpen}
                              onAdded={() => load()} />
      {/* C3a · item 10 — pick an FS head from the 80-head library. */}
      <AddHeadFromLibraryDialog open={headLibOpen} onOpenChange={setHeadLibOpen}
                                    onAdded={() => load()} />
      {/* C3a · item 11 — N/A heads live under their own accordion. */}
      {naHeads.length > 0 && (
        <Card data-testid="head-folders-na-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <EyeOff className="h-4 w-4 text-slate-500" />
              {t("headFolders.naTitle")}
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                {naHeads.length}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y rounded border" data-testid="head-folders-na-list">
              {naHeads.map((hf) => (
                <li key={hf.fs_code}
                    className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm"
                    data-testid={`head-folder-na-${hf.fs_code}`}>
                  <div className="min-w-0 flex-1">
                    <div className="break-words">
                      <span dir="ltr" className="me-1 font-mono">{hf.fs_code}</span>
                      — {(lang === "ar" ? hf.label_ar : hf.label) || hf.label}
                    </div>
                    {hf.na_reason && (
                      <div className="text-[11px] text-muted-foreground">
                        {t("headFolders.naReasonLabel")}: {hf.na_reason}
                      </div>
                    )}
                  </div>
                  <Button size="sm" variant="outline"
                          onClick={() => restoreNa(hf.fs_code)}
                          data-testid={`head-folder-na-${hf.fs_code}-restore`}>
                    <Undo2 className="h-3 w-3 me-1" /> {t("headFolders.restore")}
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      <HeadFolderWorksheetDialog />
      <SurlWorksheetDialog />
      <HeadFolderVouchingDialog />
      <WorksheetErrorBoundary clearParams={WS_OPEN_PARAM.completeness}><CompletenessWorksheetDialog /></WorksheetErrorBoundary>
      <WorksheetErrorBoundary clearParams={WS_OPEN_PARAM.inventory_count}><InventoryCountWorksheetDialog /></WorksheetErrorBoundary>
      <WorksheetErrorBoundary clearParams={WS_OPEN_PARAM.estimate}><EstimatesWorksheetDialog /></WorksheetErrorBoundary>
      <WorksheetErrorBoundary clearParams={WS_OPEN_PARAM.related_party}><RelatedPartyWorksheetDialog /></WorksheetErrorBoundary>
      <LeadSheetDialog />
      <ProceduresPerformedDialog />
      <ObservationsDialog />
      <HfDeleteDialog target={delTarget} engId={currentEngId} t={t} onClose={() => setDelTarget(null)} onDone={load} />
      <HeadFolderFormsListDialog open={!!formsListFor}
                                     onClose={() => setFormsListFor(null)}
                                     fsCode={formsListFor?.fs_code}
                                     fileId={formsListFor?.file_id}
                                     engId={currentEngId} onChanged={load}
                                     heads={heads} />
    </div>
  );
}
